import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { getCustomRenderColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import { ECSegmentListDTO, ECSegmentStatus, ECSegmentType, FINAL_STATUS, MAX_EC_SEGMENTS_OF_ADVERTISER } from 'core/ecSegment/ECSegment';
import { SessionStorageHelper, SessionStorageItemKeys } from 'helper/StorageHelper';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultECSegmentManager, ECSegmentManager } from 'core/ecSegment/ECSegmentManager';
import { camelCase, isNumber, uniq } from 'lodash';
import { renderECSegmentNameIdColumn } from './components/ECSegmentNameIdColumn';
import { renderECSegmentStatusColumn } from './components/ECSegmentStatusColumn';
import { createSelectOptions } from 'utils/SelectOptionsUtils';
import { numberWithCommas } from 'utils/StringUtil';

export enum ECSegmentListColumn {
  ID = 'id',
  ADVERTISER = 'advertiserName',
  TYPE = 'type',
  VISITORS = 'visitors',
  STATUS = 'status',
  LAST_UPDATED_TIME = 'lastUpdatedTime'
}

export const ecSegmentStatusOptions = [
  {
    label: 'ALL',
    value: -1
  },
  ...createSelectOptions(Object.values(ECSegmentStatus).filter(value => isNumber(value) && value !== ECSegmentStatus.DELETED), 'ecSegment.status.')
];

const SIZE_PER_PAGE = 10;
const ecSegmentManager: ECSegmentManager = new DefaultECSegmentManager();
export const useECSegmentListModel = () => {
  const advertiserIdFromSession = SessionStorageHelper.getItem(
    SessionStorageItemKeys.ADVERTISER
  );
  const [advertiserId, setAdvertiserId] = useState<number | undefined>(
    advertiserIdFromSession ? +advertiserIdFromSession : undefined
  );
  const [ecStatus, setECStatus] = useState<ECSegmentStatus | -1>(-1);
  const [page, setPage] = useState<number>(1);
  const [searchString, setSearchString] = useState<string>('');
  const [ecSegments, setECSegments] = useState<ECSegmentListDTO[]>([]);
  const [canAddSegment, setCanAddSegment] = useState<boolean>();
  const [ecSegemntsToDelete, setECSegmentsToDelete] = useState<string[]>();
  const [ecSegmentsToIntersect, setECSegmentsToIntersect] = useState<string[]>();
  const [selectedECSegments, setSelectedECSegments] = useState<string[]>([]);
  const [ecSegmentRulesToShow, setECSegmentRulesToShow] = useState<string>();

  const { loading, callAPIs } = useCallAPI();

  const getNumberRange = useCallback((num: number): string => {
    const numStr = num.toString();
    const length = numStr.length;

    if (length === 1) {
      return '1 ~ 9';
    }

    if (length === 2) {
      return '10 ~ 99';
    }

    const prefix = numStr.slice(0, 2);
    const postfix = ''.padEnd(length - 2, '0');
    const lowerBound = prefix + postfix;
    const upperBound = parseInt(prefix, 10) + 1 + postfix;
    return `${numberWithCommas(lowerBound)} ~ ${numberWithCommas(upperBound)}`;
  }, []);

  const columns = useMemo(() => {
    return [
      getCustomRenderColumn(
        sortableColumn(
          ECSegmentListColumn.ID,
          i18n.t<string>('ecSegmentList.headers.name'),
          false
        ),
        renderECSegmentNameIdColumn
      ),
      sortableColumn(
        ECSegmentListColumn.ADVERTISER,
        i18n.t<string>('ecSegmentList.headers.advertiser'),
        false
      ),
      {
        ...sortableColumn(
          ECSegmentListColumn.TYPE,
          i18n.t<string>('ecSegmentList.headers.type'),
          false
        ),
        formatter: (type: ECSegmentType) =>
          i18n.t<string>(`ecSegment.types.${camelCase(type)}`)
      },
      {
        ...sortableColumn(
          ECSegmentListColumn.VISITORS,
          i18n.t<string>('ecSegmentList.headers.visitors'),
          false
        ),
        formatter: getNumberRange
      },
      getCustomRenderColumn(
        sortableColumn(
          ECSegmentListColumn.STATUS,
          i18n.t<string>('ecSegmentList.headers.status'),
          false
        ),
        renderECSegmentStatusColumn
      ),
      sortableColumn(
        ECSegmentListColumn.LAST_UPDATED_TIME,
        i18n.t<string>('ecSegmentList.headers.lastUpdatedTime'),
        false
      )
    ];
  }, [getNumberRange]);

  const fetchECSegments = useCallback(
    () => {
      callAPIs(
        [
          () =>
            ecSegmentManager.getECSegments(
              advertiserId
            )
        ],
        (ecSegments: ECSegmentListDTO[]) => {
          setECSegments(ecSegments);
          setCanAddSegment(advertiserId === undefined || ecSegments.length < MAX_EC_SEGMENTS_OF_ADVERTISER);
        }
      );
    },
    [
      advertiserId,
      callAPIs
    ]
  );

  useEffect(() => {
    fetchECSegments();
  }, [fetchECSegments]);

  const deleteECSegment = useCallback((id: string) => {
    setECSegmentsToDelete([id]);
  }, []);

  const onCloseDeleteECSegmentModal = useCallback(
    (needResetList: boolean) => {
      setECSegmentsToDelete(undefined);
      if (needResetList) {
        fetchECSegments();
        setSelectedECSegments([]);
      }
    },
    [fetchECSegments]
  );

  const onCloseIntersectECSegmentModal = useCallback(
    (needResetList: boolean) => {
      setECSegmentsToIntersect(undefined);
      if (needResetList) {
        fetchECSegments();
        setSelectedECSegments([]);
      }
    },
    [fetchECSegments]
  );

  const getECSegmentsIntersectCannotIntersectReason = useCallback(
    (selectedECSegmentsData: ECSegmentListDTO[]) => {
      const statusCannotIntersect =
        selectedECSegmentsData.filter((ecSegment) =>
          Object.values(FINAL_STATUS).includes(ecSegment.status)
        ).length > 0;

      if (statusCannotIntersect) {
        return i18n.t<string>(
          'ecSegmentList.hints.archivedECSegmentCannotIntersect'
        );
      }

      const cannotIntersectAgain =
        selectedECSegmentsData.filter(
          (ecSegment) => ecSegment.type === ECSegmentType.INTERSECT
        ).length > 0;

      if (cannotIntersectAgain) {
        return i18n.t<string>('ecSegmentList.hints.cannotIntersectAgain');
      }

      const intersectLimit = selectedECSegmentsData.length !== 2;
      if (intersectLimit) {
        return i18n.t<string>('ecSegmentList.hints.intersectLimit');
      }

      const uniqAdvertiserId = uniq(
        selectedECSegmentsData.map((segment) => segment.advertiserId)
      );
      const notSameAdvertiser = uniqAdvertiserId.length > 1;
      if (notSameAdvertiser) {
        return i18n.t<string>('ecSegmentList.hints.notSameAdvertiser');
      }

      const advertiserCannotAddSegment = !notSameAdvertiser
        ? ecSegments.filter(
            (ecSegment) => ecSegment.advertiserId === uniqAdvertiserId[0]
          ).length >= MAX_EC_SEGMENTS_OF_ADVERTISER
        : true;
      if (advertiserCannotAddSegment) {
        return advertiserId
          ? i18n.t<string>('ecSegmentList.hints.cannotCreate')
          : i18n.t<string>('ecSegmentList.hints.advertiserCannotCreate');
      }
    },
    [advertiserId, ecSegments]
  );

  const batchOperations = useMemo(() => {
    const selectedECSegmentsData = ecSegments.filter(ecSegment =>
      selectedECSegments.includes(ecSegment.id)
    );
    const cannotDelete = selectedECSegmentsData.filter(
      (ecSegment) =>
        ecSegment.status === ECSegmentStatus.ACTIVE
    ).length > 0;

    const cannotIntersectReason = getECSegmentsIntersectCannotIntersectReason(selectedECSegmentsData);

    return [
      {
        operation: {
          text: i18n.t<string>('ecSegmentList.buttons.intersect'),
          hint: cannotIntersectReason ? cannotIntersectReason : '',
          enable: cannotIntersectReason === undefined,
          onClick: () => setECSegmentsToIntersect(selectedECSegments)
        }
      },
      {
        operation: {
          text: i18n.t<string>('common.buttons.delete'),
          hint: cannotDelete
            ? i18n.t<string>('ecSegmentList.hints.cannotDelete')
            : '',
          enable: !cannotDelete,
          onClick: () => setECSegmentsToDelete(selectedECSegments)
        }
      }
    ];
  }, [ecSegments, selectedECSegments, getECSegmentsIntersectCannotIntersectReason]);

  const onHandleSelect = useCallback((id: string | number, select: boolean) => {
    setSelectedECSegments(prev => select ? uniq([...prev, id.toString()]) : prev.filter(p => p !== id.toString()));
  }, []);

  const filteredECSegments = useMemo(() => {
    return ecSegments.filter(ecSegment => {
      const searchMatch = searchString === '' ||
        ecSegment.name.toLowerCase().includes(searchString.toLowerCase()) ||
        ecSegment.id.includes(searchString.toLowerCase());
      const statusMatch = ecStatus === -1 || ecSegment.status === ecStatus;
      return searchMatch && statusMatch;
    });
  }, [ecSegments, searchString, ecStatus]);

  const onHandleSelectPage = useCallback((select: boolean) => {
    const allId = filteredECSegments.map(ecSegment => ecSegment.id);
    const offset = (page - 1) * SIZE_PER_PAGE;
    const end = offset + SIZE_PER_PAGE;
    const targetId = allId.slice(offset, end);
    setSelectedECSegments(prev => select ?
      uniq([...prev, ...targetId]) :
      prev.filter(p => !targetId.includes(p))
    );
  }, [page, filteredECSegments]);

  const onHandleSearch = useCallback((searchString: string) => {
    setSearchString(searchString);
  }, []);

  const onCloseECSegmentRulesModal = useCallback(() => {
    setECSegmentRulesToShow(undefined);
  }, []);

  const onRemoveAllSelect = useCallback(() => {
    setSelectedECSegments([]);
  }, []);

  return {
    page,
    loading,
    columns,
    ecStatus,
    ecSegments,
    advertiserId,
    sizePerPage: SIZE_PER_PAGE,
    searchString,
    canAddSegment,
    batchOperations,
    filteredECSegments,
    selectedECSegments,
    ecSegemntsToDelete,
    ecSegmentRulesToShow,
    ecSegmentsToIntersect,
    setPage,
    setECStatus,
    onHandleSelect,
    deleteECSegment,
    setAdvertiserId,
    onRemoveAllSelect,
    onHandleSelectPage,
    onHandleSearch,
    onCloseECSegmentRulesModal,
    onCloseDeleteECSegmentModal,
    onCloseIntersectECSegmentModal
  };
};
